'use client';

import type { SessionResponse } from '@/data/code-sessions/types';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useRouter } from 'next/navigation';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { newCodeSessionHref } from './code-shell';

import { useArchiveCodeSession } from '@/data/code-sessions/mutations';
import { codeSessionKeys } from '@/data/code-sessions/queries';
import { capabilitiesOf } from '@/data/code-sessions/runtime/capabilities';
import { listsFor } from '@/data/code-sessions/runtime/session-lists';

/** Open sessions this tab archived and is leaving. */
const leaving = new Set<string>();
const listeners = new Set<() => void>();

function setLeaving(sessionId: string, left: boolean) {
  if (leaving.has(sessionId) === left) {
    return;
  }
  if (left) {
    leaving.add(sessionId);
  } else {
    leaving.delete(sessionId);
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Whether this tab archived the open session and is leaving it, so its page keeps its last look
 * rather than redraw as archived on the way out. Next hides or unmounts a page the person has
 * left, which runs this effect's cleanup, so a page shown again reads as archived.
 */
export function useLeavingArchivedSession(sessionId: string): boolean {
  const left = useSyncExternalStore(
    subscribe,
    () => leaving.has(sessionId),
    () => false,
  );
  useEffect(() => () => setLeaving(sessionId, false), [sessionId]);
  return left;
}

/** Leave an archived session immediately; a failed request restores its sidebar entry. */
export function useArchiveSessionAction() {
  const { mutate } = useArchiveCodeSession();
  const router = useRouter();
  const client = useQueryClient();
  const { sessionId: activeId } = useParams<{ sessionId?: string }>();
  return useCallback(
    (sessionId: string) => {
      const open = activeId === sessionId;
      if (open) {
        setLeaving(sessionId, true);
      }
      mutate(sessionId);
      if (open) {
        // A new session where the archived one ran, as T3 opens a draft in the archived thread's
        // project: its repository, or the place the landing last left, for a folder on this
        // computer names no repository.
        const session =
          client.getQueryData<SessionResponse>(codeSessionKeys.detail(sessionId))?.session ??
          listsFor(sessionId).find(client, sessionId);
        router.push(
          newCodeSessionHref(
            session && capabilitiesOf(session).github ? session.repoFullName : null,
          ),
        );
      }
      // Request completion never redirects: the user may have moved on to another session.
    },
    [activeId, client, mutate, router],
  );
}
